import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ArrowDown, Hash, MessageSquare } from 'lucide-react';
import { Permission } from '@spawnwire/shared';
import { Button, clsx, Spinner } from '../../components/ui';
import { errorMessage } from '../../lib/api';
import { ackChannel, loadAround, loadLatest, loadNewer, loadOlder } from '../../lib/data';
import { can, useChannelPermissions } from '../../lib/perms';
import { formatDay, sameDay } from '../../lib/time';
import { isActive } from '../../lib/viewing';
import { privateChannelName, useStore, type UiMessage } from '../../store/store';
import { ui, useUi } from '../../store/ui';
import { MessageItem } from './MessageItem';

const GROUP_WINDOW_MS = 7 * 60 * 1000;

function shouldGroup(prev: UiMessage | undefined, cur: UiMessage): boolean {
  if (!prev || !prev.author || !cur.author) return false;
  if (prev.author.id !== cur.author.id) return false;
  if (cur.type !== 'default' || (prev.type !== 'default' && prev.type !== 'reply')) return false;
  // A bot's answer to a slash command shows who ran it, so it starts its own group.
  if (cur.interaction) return false;
  if (!sameDay(prev.createdAt, cur.createdAt)) return false;
  return new Date(cur.createdAt).getTime() - new Date(prev.createdAt).getTime() < GROUP_WINDOW_MS;
}

function ChannelIntro({ channelId }: { channelId: string }) {
  const channel = useStore((s) => s.channels[channelId]);
  const name = useStore((s) => (channel ? privateChannelName(s, channel) : ''));
  if (!channel) return null;
  if (channel.type === 'dm' || channel.type === 'group_dm') {
    return (
      <div className="px-4 pb-4 pt-8">
        <div className="mb-3 flex size-16 items-center justify-center rounded-full bg-accent-soft text-accent">
          <MessageSquare className="size-8" aria-hidden />
        </div>
        <h2 className="text-2xl font-bold text-fg">{name}</h2>
        <p className="mt-1 text-sm text-muted">This is the beginning of your conversation{channel.type === 'group_dm' ? ' with this group' : ''}.</p>
      </div>
    );
  }
  return (
    <div className="px-4 pb-4 pt-8">
      <div className="mb-3 flex size-16 items-center justify-center rounded-full bg-active text-fg">
        <Hash className="size-9" aria-hidden />
      </div>
      <h2 className="text-2xl font-bold text-fg">{channel.type === 'thread' ? channel.name : `Welcome to #${channel.name}`}</h2>
      <p className="mt-1 text-sm text-muted">{channel.type === 'thread' ? 'This is the start of the thread.' : `This is the start of the #${channel.name} channel.`} {channel.topic}</p>
    </div>
  );
}

export function MessageList({ channelId }: { channelId: string }) {
  const channel = useStore((s) => s.channels[channelId]);
  const list = useStore((s) => s.messages[channelId]);
  const meId = useStore((s) => s.me?.id);
  const perms = useChannelPermissions(channelId);
  const jumpTo = useUi((s) => s.jumpTo);
  const scrollRef = useRef<HTMLDivElement>(null);
  const atBottomRef = useRef(true);
  const prevFirstRef = useRef<string | null>(null);
  const prevHeightRef = useRef(0);
  const initialScrollDone = useRef(false);
  const [atBottom, setAtBottom] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [highlight, setHighlight] = useState<string | null>(null);
  // The read marker when the channel was opened; messages after it get a "New" divider.
  const [readMarker] = useState(() => useStore.getState().readStates[channelId]?.lastReadMessageId ?? null);

  const canRead = can(perms, Permission.READ_MESSAGE_HISTORY);
  const isPrivate = channel ? !channel.spaceId : false;
  const spaceId = channel?.spaceId ?? null;
  const canThread = !!channel && (channel.type === 'text' || channel.type === 'announcement') && can(perms, Permission.CREATE_THREADS);

  useEffect(() => {
    if (!canRead || list?.loaded) return;
    loadLatest(channelId).catch((err) => setError(errorMessage(err)));
  }, [channelId, canRead, list?.loaded]);

  // Jump to a specific message (from search, pins, replies or a link).
  useEffect(() => {
    if (!jumpTo || jumpTo.channelId !== channelId) return;
    const { messageId } = jumpTo;
    ui.clearJump();
    const reveal = () => {
      const el = document.getElementById(`message-${messageId}`);
      if (el) {
        el.scrollIntoView({ block: 'center' });
        setHighlight(messageId);
        setTimeout(() => setHighlight((h) => (h === messageId ? null : h)), 2000);
        return true;
      }
      return false;
    };
    if (!reveal()) {
      loadAround(channelId, messageId)
        .then(() => requestAnimationFrame(() => reveal() || ui.toast('That message is no longer available.')))
        .catch((err) => ui.toast(errorMessage(err), 'error'));
    }
  }, [jumpTo, channelId]);

  const ids = list?.ids ?? [];
  const firstId = ids[0] ?? null;

  // Keep the viewport anchored: when older messages are prepended, offset the
  // scroll by the added height; when new ones arrive at the bottom, follow them.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || !list?.loaded) return;
    if (!initialScrollDone.current) {
      initialScrollDone.current = true;
      const divider = el.querySelector('[data-unread-divider]');
      if (divider) (divider as HTMLElement).scrollIntoView({ block: 'center' });
      else el.scrollTop = el.scrollHeight;
    } else if (prevFirstRef.current && firstId !== prevFirstRef.current && ids.includes(prevFirstRef.current)) {
      el.scrollTop += el.scrollHeight - prevHeightRef.current;
    } else if (atBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
    prevFirstRef.current = firstId;
    prevHeightRef.current = el.scrollHeight;
  });

  const maybeAck = useCallback(() => {
    const state = useStore.getState();
    const c = state.channels[channelId];
    const l = state.messages[channelId];
    if (atBottomRef.current && l?.atLatest && isActive()) ackChannel(c);
  }, [channelId]);

  useEffect(() => {
    maybeAck();
  }, [ids.length, maybeAck, channel?.lastMessageId]);

  useEffect(() => {
    window.addEventListener('focus', maybeAck);
    document.addEventListener('visibilitychange', maybeAck);
    return () => {
      window.removeEventListener('focus', maybeAck);
      document.removeEventListener('visibilitychange', maybeAck);
    };
  }, [maybeAck]);

  // Images load after layout; keep pinned to the bottom while they do.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      if (atBottomRef.current) el.scrollTop = el.scrollHeight;
      prevHeightRef.current = el.scrollHeight;
    });
    for (const child of Array.from(el.children)) observer.observe(child);
    return () => observer.disconnect();
  }, [list?.loaded]);

  function onScroll() {
    const el = scrollRef.current;
    if (!el) return;
    const bottom = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
    atBottomRef.current = bottom;
    if (bottom !== atBottom) setAtBottom(bottom);
    if (el.scrollTop < 400 && list?.hasMoreBefore && !list.loadingBefore) {
      prevHeightRef.current = el.scrollHeight;
      void loadOlder(channelId);
    }
    if (bottom) {
      if (list && !list.atLatest) void loadNewer(channelId);
      maybeAck();
    }
  }

  function jumpToPresent() {
    const el = scrollRef.current;
    if (list && !list.atLatest) {
      initialScrollDone.current = false;
      void loadLatest(channelId);
    }
    atBottomRef.current = true;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }

  if (!canRead) {
    return (
      <div className="flex flex-1 items-center justify-center p-8 text-center text-sm text-muted">
        You do not have permission to read the history of this channel. New messages will still appear here while you are viewing it.
      </div>
    );
  }

  let dividerPlaced = false;
  const unreadCount = readMarker ? ids.filter((id) => !id.startsWith('pending:') && id > readMarker && list?.byId[id]?.author?.id !== meId).length : 0;

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      {unreadCount > 0 && !atBottom && (
        <button type="button" className="absolute inset-x-4 top-2 z-10 flex items-center justify-between rounded-md bg-accent px-3 py-1 text-xs font-semibold text-accent-fg shadow-pop" onClick={() => document.querySelector('[data-unread-divider]')?.scrollIntoView({ block: 'center', behavior: 'smooth' })}>
          <span>
            {unreadCount}
            {list?.hasMoreBefore && unreadCount >= 50 ? '+' : ''} new message{unreadCount === 1 ? '' : 's'} since you last read
          </span>
          <span>Jump to first unread</span>
        </button>
      )}
      <div ref={scrollRef} onScroll={onScroll} className="scroll-thin min-h-0 flex-1 overflow-y-auto overflow-x-hidden" role="log" aria-live="polite" aria-relevant="additions" aria-label="Messages">
        <div className="flex min-h-full flex-col justify-end pb-4">
          {!list?.loaded ? (
            error ? (
              <div className="p-8 text-center">
                <p className="text-sm text-danger">{error}</p>
                <Button className="mt-3" variant="secondary" onClick={() => loadLatest(channelId).catch((err) => setError(errorMessage(err)))}>
                  Try again
                </Button>
              </div>
            ) : (
              <div className="space-y-4 p-4" aria-busy="true">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="flex animate-pulse gap-4">
                    <div className="size-10 rounded-full bg-active" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3 w-32 rounded bg-active" />
                      <div className="h-3 rounded bg-active" style={{ width: `${40 + ((i * 37) % 50)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            <>
              {list.hasMoreBefore ? (
                <div className="flex justify-center py-4">{list.loadingBefore ? <Spinner /> : <span className="h-5" />}</div>
              ) : (
                <ChannelIntro channelId={channelId} />
              )}
              {ids.map((id, i) => {
                const m = list.byId[id]!;
                const prev = i > 0 ? list.byId[ids[i - 1]!] : undefined;
                const newDay = !prev || !sameDay(prev.createdAt, m.createdAt);
                const isUnreadStart = !dividerPlaced && readMarker !== null && !id.startsWith('pending:') && id > readMarker && m.author?.id !== meId;
                if (isUnreadStart) dividerPlaced = true;
                return (
                  <Fragment key={id}>
                    {newDay && (
                      <div className="mx-4 my-3 flex items-center gap-2 text-xs font-semibold text-faint" role="separator">
                        <span className="h-px flex-1 bg-line" />
                        {formatDay(m.createdAt)}
                        <span className="h-px flex-1 bg-line" />
                      </div>
                    )}
                    {isUnreadStart && (
                      <div data-unread-divider className="mx-4 my-1 flex items-center gap-2 text-[11px] font-bold uppercase text-danger" role="separator" aria-label="New messages">
                        <span className="h-px flex-1 bg-danger" />
                        New
                      </div>
                    )}
                    <MessageItem
                      message={m}
                      grouped={!newDay && !isUnreadStart && shouldGroup(prev, m)}
                      spaceId={spaceId}
                      perms={perms}
                      highlighted={highlight === id}
                      canThread={canThread}
                      isPrivate={isPrivate}
                      announcement={channel?.type === 'announcement'}
                    />
                  </Fragment>
                );
              })}
            </>
          )}
        </div>
      </div>
      {(!atBottom || (list && !list.atLatest)) && list?.loaded && (
        <button
          type="button"
          onClick={jumpToPresent}
          className={clsx('absolute bottom-3 right-6 flex items-center gap-1.5 rounded-full bg-overlay px-3 py-1.5 text-xs font-semibold text-fg shadow-pop ring-1 ring-line hover:bg-elevated')}
        >
          <ArrowDown className="size-3.5" aria-hidden /> Jump to present
        </button>
      )}
    </div>
  );
}
